import { useEffect, useState } from "react";
import { Button, Form, Input, Modal, Radio, Select, Tag, Typography } from "antd";
import { ArrowDownOutlined, ArrowUpOutlined, DeleteOutlined, PlusOutlined, SettingOutlined } from "@ant-design/icons";
import { Box } from "@mui/material";
import SearchModal from "../../SearchModal";
import ParamsPicklist from "../../../Types/ParamsPicklist";
import axios from "axios";
import { Crypter } from "../../../Functions/Security/Crypter";
import { OpeApi } from "../../../Services/ApiOpeLiees";

const searchOperat: ParamsPicklist = { CodeAgence: '', Table: "OPERAT", Colonne1: "CODOPE", Colonne2: "LIBELL", Colagence: '101', Choix: 22 };

interface GroupeOption {
    IDGRP: number;
    CODGRP: string;
    LIBGRP: string;
    LOGIQUE: string;
}

const GroupeBadge = (props: { form: any; index: number }) => {
    const gid = Form.useWatch(["OPERELIES", props.index, "GROUPE_ID"], props.form);
    const glib = Form.useWatch(["OPERELIES", props.index, "GROUPE_LIB"], props.form);
    return <Tag color={gid ? "blue" : "default"} style={{ marginRight: 4 }}>{gid ? (glib ?? `G${gid}`) : "—"}</Tag>;
};

const OperationsLiees = (props: { form: any; disabled?: boolean; authHeader: () => string }) => {

    const [formLiees] = Form.useForm();
    const [modalOpen, setModalOpen] = useState(false);
    const [rowIndex, setRowIndex] = useState<number>(0);
    const [selGroupe, setSelGroupe] = useState<number | null>(null);
    const [selBase, setSelBase] = useState<string>("PRECEDENT");
    const [groupes, setGroupes] = useState<GroupeOption[]>([]);

    const liees: any[] = Form.useWatch("OPERELIES", props.form) ?? [];

    // Chargement des groupes de conditions reels (remplace le mock)
    useEffect(() => {
        axios.request({
            method: 'get',
            maxBodyLength: Infinity,
            url: OpeApi.ReadGroupes,
            headers: { 'Authorization': props.authHeader(), 'Content-Type': 'application/json' }
        }).then((response) => {
            if (response.data) {
                const res = JSON.parse(Crypter.decrypt(response.data));
                if (res.StatutCode === 200 && res.Result?.Table) {
                    setGroupes(res.Result.Table.filter((g: any) => g.ACTIVE));
                }
            }
        }).catch((err) => console.log("ReadGroupes:", err));
    }, []);

    useEffect(() => {
        liees.forEach((r, i) => {
            formLiees.setFieldsValue({
                ["OPLIE_" + i]: r?.CODELIE ?? "",
                ["lib_OPLIE_" + i]: r?.LIBELIE ?? ""
            });
        });
    }, [liees]);

    const syncHidden = (index: number, code: string, libelle?: string) => {
        props.form.setFieldValue(["OPERELIES", index, "CODELIE"], code ?? "");
        props.form.setFieldValue(["OPERELIES", index, "LIBELIE"], libelle ?? formLiees.getFieldValue("lib_OPLIE_" + index) ?? "");
    };

    const openModalGroupe = (index: number) => {
        setRowIndex(index);
        setSelGroupe(props.form.getFieldValue(["OPERELIES", index, "GROUPE_ID"]) ?? null);
        setSelBase(props.form.getFieldValue(["OPERELIES", index, "BASE_CALCUL"]) ?? "PRECEDENT");
        setModalOpen(true);
    };

    const saveModalGroupe = () => {
        const g = groupes.find(x => x.IDGRP === selGroupe);
        props.form.setFieldValue(["OPERELIES", rowIndex, "GROUPE_ID"], selGroupe);
        props.form.setFieldValue(["OPERELIES", rowIndex, "GROUPE_LIB"], g ? `${g.CODGRP} — ${g.LIBGRP}` : null);
        props.form.setFieldValue(["OPERELIES", rowIndex, "BASE_CALCUL"], selBase);
        setModalOpen(false);
    };

    return (
        <>
            <Form.List name="OPERELIES">
                {(fields, { add, remove, move }) => (
                    <>
                        {fields.length === 0 && (
                            <Box style={{ textAlign: "center", color: "#999", padding: "8px 0", fontSize: 12 }}>
                                Aucune opération reliée
                            </Box>
                        )}
                        {fields.map((field, idx) => (
                            <Box key={field.key} style={{ display: "flex", alignItems: "center", marginBottom: 4 }}>
                                <Tag color="#116599" style={{ minWidth: 26, textAlign: "center", marginRight: 4 }}>{idx + 1}</Tag>
                                <Box style={{ flexGrow: 1, minWidth: 0 }}>
                                    <Form.Item name={[field.name, "CODELIE"]} hidden><Input /></Form.Item>
                                    <Form.Item name={[field.name, "LIBELIE"]} hidden><Input /></Form.Item>
                                    <Form.Item name={[field.name, "GROUPE_ID"]} hidden><Input /></Form.Item>
                                    <Form.Item name={[field.name, "GROUPE_LIB"]} hidden><Input /></Form.Item>
                                    <Form.Item name={[field.name, "BASE_CALCUL"]} hidden><Input /></Form.Item>
                                    <Form form={formLiees} component={false} disabled={props.disabled}>
                                        <SearchModal
                                            width={0}
                                            Titre="Recherche opération"
                                            notRequired
                                            label={`Opération reliée ${idx + 1}`}
                                            name={"OPLIE_" + idx}
                                            Parametre={searchOperat}
                                            Taille1={"100%"}
                                            Taille2={"100%"}
                                            form={formLiees}
                                            handleFunction={(code: string, lib?: string) => syncHidden(idx, code, lib)}
                                        />
                                    </Form>
                                </Box>
                                <GroupeBadge form={props.form} index={idx} />
                                <Button size="small" icon={<SettingOutlined />} title="Groupe de conditions / base de calcul" onClick={() => openModalGroupe(idx)} />
                                <Button size="small" icon={<ArrowUpOutlined />} title="Monter" disabled={idx === 0} onClick={() => move(idx, idx - 1)} />
                                <Button size="small" icon={<ArrowDownOutlined />} title="Descendre" disabled={idx === fields.length - 1} onClick={() => move(idx, idx + 1)} />
                                <Button size="small" danger icon={<DeleteOutlined />} title="Retirer" onClick={() => remove(idx)} />
                            </Box>
                        ))}
                        <Button size="small" icon={<PlusOutlined />} onClick={() => add({ CODELIE: "", LIBELIE: "", GROUPE_ID: null, BASE_CALCUL: "PRECEDENT" })} block>
                            Ajouter une opération reliée
                        </Button>
                    </>
                )}
            </Form.List>

            <Modal
                title="Conditionnement de l'opération reliée"
                open={modalOpen}
                onOk={saveModalGroupe}
                onCancel={() => setModalOpen(false)}
                okText="Valider"
                okButtonProps={{ style: { color: "#fff" } }}
                cancelText="Annuler"
                zIndex={2300}
            >
                <Typography.Text type="secondary" style={{ fontSize: 12, display: "block", marginBottom: 4 }}>
                    Groupe de conditions (vide = exécution inconditionnelle) :
                </Typography.Text>
                <Select
                    style={{ width: "100%", marginBottom: 16 }}
                    allowClear
                    placeholder="Aucun groupe — inconditionnel"
                    value={selGroupe}
                    onChange={(v) => setSelGroupe(v ?? null)}
                    options={groupes.map(g => ({
                        label: `${g.CODGRP} — ${g.LIBGRP} (${g.LOGIQUE})`,
                        value: g.IDGRP
                    }))}
                />
                <Typography.Text type="secondary" style={{ fontSize: 12, display: "block", marginBottom: 4 }}>
                    Base de calcul du montant :
                </Typography.Text>
                <Radio.Group
                    optionType="button"
                    buttonStyle="solid"
                    value={selBase}
                    onChange={(e) => setSelBase(e.target.value)}
                >
                    <Radio.Button value="PRECEDENT">Étape précédente (cascade)</Radio.Button>
                    <Radio.Button value="INITIAL">Montant initial</Radio.Button>
                </Radio.Group>
            </Modal>
        </>
    );
};

export default OperationsLiees;
